/**
 * Frontend mirror of dashboard/utils/dataGridPeriods.js.
 * Derives typed period arrays from Form.io person_data.dataGrid rows.
 *
 * Travel nests dates; Accommodation keeps them flat.
 * Historical keys: departureDateTime1 = arrival, departureDateTime = departure.
 *
 * Date handling: submitted values carry the *submitter's* browser offset, not LA's — see
 * the backend copy's header comment. We trust the ISO date part as the intended LA day
 * (travelDateTimeToLADateKey) and re-anchor it to LA midnight (laDateKeyToUTC), matching
 * what the Booking tab reads everything through (utcToLADateKey).
 */

import { travelDateTimeToLADateKey, laDateKeyToUTC } from './laDateUtils';

const ARRIVAL_PATH = ['arrivalFrom', 'departureDateTime1'];
const DEPARTURE_PATH = ['departureFromLosAngeles', 'departureDateTime'];
const CHECK_IN_PATH = ['checkInDate'];
const CHECK_OUT_PATH = ['checkOutDate'];

function readPath(row, path) {
  let current = row;
  for (const key of path) {
    if (!current || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return current;
}

function toDateOrNull(value) {
  const key = travelDateTimeToLADateKey(value);
  if (!key) return null;
  const date = laDateKeyToUTC(key);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Prefer nested Form.io path; fall back to flat key used by some schemas. */
function readDate(row, nestedPath, flatKey) {
  return toDateOrNull(readPath(row, nestedPath)) || toDateOrNull(row?.[flatKey]);
}

/**
 * @param {unknown} dataGrid
 * @returns {{arrival: Array<{arrival_date: Date, departure_date: Date}>,
 *            stay: Array<{check_in_date: Date, check_out_date: Date}>}}
 */
export function derivePeriodsFromDataGrid(dataGrid) {
  const rows = Array.isArray(dataGrid) ? dataGrid : [];
  const arrival = [];
  const stay = [];
  const seenArrival = new Set();
  const seenStay = new Set();

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;

    const arrivalDate = readDate(row, ARRIVAL_PATH, 'departureDateTime1');
    const departureDate = readDate(row, DEPARTURE_PATH, 'departureDateTime');
    if (arrivalDate && departureDate) {
      const key = `${arrivalDate.getTime()}|${departureDate.getTime()}`;
      if (!seenArrival.has(key)) {
        seenArrival.add(key);
        arrival.push({ arrival_date: arrivalDate, departure_date: departureDate });
      }
    }

    const checkInDate = readDate(row, CHECK_IN_PATH, 'checkInDate');
    const checkOutDate = readDate(row, CHECK_OUT_PATH, 'checkOutDate');
    if (checkInDate && checkOutDate) {
      const key = `${checkInDate.getTime()}|${checkOutDate.getTime()}`;
      if (!seenStay.has(key)) {
        seenStay.add(key);
        stay.push({ check_in_date: checkInDate, check_out_date: checkOutDate });
      }
    }
  }

  return { arrival, stay };
}
